import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "col-customize" }
const _hoisted_2 = {
  class: "col-customize__panel",
  role: "dialog",
  "aria-label": "Customize table columns"
}
const _hoisted_3 = { class: "col-customize__header" }
const _hoisted_4 = { class: "col-customize__title-row" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "col-customize__subtitle" }
const _hoisted_7 = {
  class: "col-customize__toggle",
  for: "col-only-selected-toggle"
}
const _hoisted_8 = {
  key: 0,
  class: "col-customize__selectall"
}
const _hoisted_9 = { class: "col-customize__fields" }
const _hoisted_10 = ["data-locked", "data-disabled"]
const _hoisted_11 = ["aria-label"]
const _hoisted_12 = ["for"]
const _hoisted_13 = {
  key: 1,
  class: "col-customize__list"
}
const _hoisted_14 = ["data-locked", "data-disabled"]
const _hoisted_15 = ["aria-label"]
const _hoisted_16 = ["for"]
const _hoisted_17 = {
  key: 1,
  class: "col-customize__footer"
}
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]

import {
    computed, ref, onMounted, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import Popover from 'primevue/popover';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import Draggable from 'vuedraggable';
import { setCookie, getCookieJSON } from '@/utils/cookieUtils';
import {
    normalizeTableColumnSettings,
    type ColumnCustomizeOption,
    type TableColumnSettings,
} from '@/composables/useTableColumns';
import { useTableColumnPreferencesStore } from '@/stores/tableColumnPreferences';
import {
    TableColumnPreferenceId,
    type TableColumnPreferenceIdValue,
} from '@/services/tableColumnPreferenceService';
import IconDrag from '@/components/icons/IconDrag.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import ToolbarIconButton from './ToolbarIconButton.vue';

type PersistenceMode = 'cookie' | 'account';
type PersistenceAction = 'save' | 'reset';

interface Props {
    columns: ColumnCustomizeOption[];
    visibleColumnKeys: string[];
    columnOrder: string[];
    lockedColumnKeys?: string[];
    disabledColumnKeys?: string[];
    tableId?: string;
    persistence?: PersistenceMode;
    disabled?: boolean;
}

const COOKIE_PREFIX = 'table_columns_';
const COOKIE_EXPIRY_DAYS = 365;

interface ToolbarIconButtonInstance {
    buttonEl: HTMLButtonElement | null;
}

interface PopoverInstance {
    visible?: boolean;
    toggle: (event: MouseEvent, target?: HTMLElement | null) => void;
    hide: () => void;
}

interface DraggableMoveEvent {
    draggedContext: {
        element: string;
    };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ColumnCustomizeControl',
  props: {
    columns: {},
    visibleColumnKeys: {},
    columnOrder: {},
    lockedColumnKeys: { default: () => [] },
    disabledColumnKeys: { default: () => [] },
    tableId: { default: undefined },
    persistence: { default: 'cookie' },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:visibleColumnKeys", "update:columnOrder", "persistenceError"],
  setup(__props: any, { emit: __emit }) {

/* eslint-env vue/setup-compiler-macros */
const props = __props;

const emit = __emit;

const preferenceStore = useTableColumnPreferencesStore();
const isAccountPersistence = computed(() => props.persistence === 'account');

function getCookieName(): string | null {
    return props.tableId ? `${COOKIE_PREFIX}${props.tableId}` : null;
}

function saveSettingsToCookie(settings: TableColumnSettings): void {
    const cookieName = getCookieName();
    if (!cookieName) return;
    try {
        setCookie(
            cookieName,
            settings as unknown as Record<string, unknown>,
            { days: COOKIE_EXPIRY_DAYS },
        );
    } catch (e) {
        console.warn('Failed to save column settings to cookie:', e);
    }
}

function loadSettingsFromCookie(): TableColumnSettings | null {
    const cookieName = getCookieName();
    if (!cookieName) return null;
    try {
        return getCookieJSON<TableColumnSettings>(cookieName);
    } catch (e) {
        console.warn('Failed to load column settings from cookie:', e);
        return null;
    }
}

const defaultColumnKeys = computed<string[]>(() => props.columns.map((c) => c.key));
const lockedColumnKeys = computed<string[]>(() => props.lockedColumnKeys);
const disabledColumnKeys = computed<string[]>(() => props.disabledColumnKeys);

function buildLabelMap(columns: ColumnCustomizeOption[]): Record<string, string> {
    return columns.reduce<Record<string, string>>((acc, col) => {
        acc[col.key] = col.label;
        return acc;
    }, {});
}

const labelByKey = computed<Record<string, string>>(() => buildLabelMap(props.columns));

const triggerRef = ref<ToolbarIconButtonInstance | null>(null);
const popoverRef = ref<PopoverInstance | null>(null);

const draftVisibleColumnKeys = ref<string[]>([]);
const draftColumnOrder = ref<string[]>([]);
const draftOnlyShowSelected = ref(false);
const draftSearch = ref('');

const totalColumns = computed(() => defaultColumnKeys.value.filter(
    (key) => !disabledColumnKeys.value.includes(key),
).length);
const draftVisibleCount = computed(() => draftVisibleColumnKeys.value.filter(
    (key) => !disabledColumnKeys.value.includes(key),
).length);
const accountBusy = computed(() => preferenceStore.saving || preferenceStore.resetting);
const triggerDisabled = computed(() => props.disabled || accountBusy.value);

const selectableColumnKeys = computed(() => (
    defaultColumnKeys.value.filter((key) => (
        !lockedColumnKeys.value.includes(key) && !disabledColumnKeys.value.includes(key)
    ))
));

function ensureLockedVisible(keys: string[]): string[] {
    const next = [...keys];
    lockedColumnKeys.value.forEach((lockedKey) => {
        if (!lockedKey) return;
        if (next.indexOf(lockedKey) !== -1) return;
        next.push(lockedKey);
    });
    const unique: string[] = [];
    next.forEach((key) => {
        if (!key) return;
        if (unique.indexOf(key) !== -1) return;
        unique.push(key);
    });
    return unique;
}

const isDraftSelected = (key: string) => draftVisibleColumnKeys.value.includes(key);
const isLockedColumn = (key: string) => lockedColumnKeys.value.includes(key);
const isDisabledColumn = (key: string) => disabledColumnKeys.value.includes(key);

const filteredDraftKeys = computed(() => {
    const query = draftSearch.value.trim().toLowerCase();
    const baseKeys = draftOnlyShowSelected.value
        ? draftColumnOrder.value.filter((key) => (
            isDraftSelected(key) && !isDisabledColumn(key)
        ))
        : draftColumnOrder.value;

    if (!query) return baseKeys;

    return baseKeys.filter((key) => {
        const label = labelByKey.value[key] ?? key;
        return label.toLowerCase().includes(query);
    });
});

const selectAllState = computed<'none' | 'partial' | 'all'>(() => {
    const selectedCount = selectableColumnKeys.value.filter(isDraftSelected).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === selectableColumnKeys.value.length) return 'all';
    return 'partial';
});

const selectAllIndeterminate = computed(() => selectAllState.value === 'partial');

const selectAllModel = computed<boolean>({
    get: () => selectAllState.value === 'all',
    set: (checked) => {
        const preservedDisabled = draftVisibleColumnKeys.value.filter(isDisabledColumn);
        draftVisibleColumnKeys.value = ensureLockedVisible(checked
            ? [...preservedDisabled, ...selectableColumnKeys.value]
            : preservedDisabled);
    },
});

const normalizedDraft = computed(() => normalizeTableColumnSettings(
    {
        visibleColumnKeys: draftVisibleColumnKeys.value,
        columnOrder: draftColumnOrder.value,
    },
    props.columns,
    lockedColumnKeys.value,
));

const normalizedSaved = computed(() => normalizeTableColumnSettings(
    {
        visibleColumnKeys: props.visibleColumnKeys,
        columnOrder: props.columnOrder,
    },
    props.columns,
    lockedColumnKeys.value,
));

const isDraftDirty = computed(() => (
    JSON.stringify(normalizedDraft.value) !== JSON.stringify(normalizedSaved.value)
));

function syncDraftFromSaved() {
    draftVisibleColumnKeys.value = normalizedSaved.value.visibleColumnKeys;
    draftColumnOrder.value = normalizedSaved.value.columnOrder;
    draftOnlyShowSelected.value = false;
    draftSearch.value = '';
}

// The draft is refreshed before the panel opens rather than on the popover's show event, so the
// first frame it renders already holds the saved columns instead of an empty list.
function onTriggerClick(event: MouseEvent) {
    if (triggerDisabled.value) return;
    if (!popoverRef.value?.visible) {
        syncDraftFromSaved();
    }

    popoverRef.value?.toggle(event, triggerRef.value?.buttonEl);
}

onMounted(() => {
    if (isAccountPersistence.value) return;

    const savedSettings = loadSettingsFromCookie();
    if (savedSettings) {
        const normalized = normalizeTableColumnSettings(
            savedSettings,
            props.columns,
            lockedColumnKeys.value,
        );

        if (JSON.stringify(normalized.visibleColumnKeys) !== JSON.stringify(props.visibleColumnKeys)) {
            emit('update:visibleColumnKeys', normalized.visibleColumnKeys);
        }
        if (JSON.stringify(normalized.columnOrder) !== JSON.stringify(props.columnOrder)) {
            emit('update:columnOrder', normalized.columnOrder);
        }
    }
});

function applyCookieChanges() {
    const { visibleColumnKeys, columnOrder } = normalizedDraft.value;

    saveSettingsToCookie({ visibleColumnKeys, columnOrder });

    emit('update:visibleColumnKeys', visibleColumnKeys);
    emit('update:columnOrder', columnOrder);
}

function getAccountTableId(): TableColumnPreferenceIdValue {
    if (props.tableId !== TableColumnPreferenceId.PAYMENTS) {
        throw new Error('Account column persistence requires a supported table id.');
    }
    return props.tableId;
}

async function saveAccountSettings() {
    if (!isDraftDirty.value || accountBusy.value) return;

    try {
        await preferenceStore.save(getAccountTableId(), normalizedDraft.value);
        popoverRef.value?.hide();
    } catch (error) {
        emit('persistenceError', 'save', error);
    }
}

async function resetToDefaults() {
    if (isAccountPersistence.value) {
        if (accountBusy.value) return;
        try {
            await preferenceStore.reset(getAccountTableId());
            popoverRef.value?.hide();
        } catch (error) {
            emit('persistenceError', 'reset', error);
        }
        return;
    }

    draftVisibleColumnKeys.value = ensureLockedVisible([...defaultColumnKeys.value]);
    draftColumnOrder.value = [...defaultColumnKeys.value];
}

function cancelAccountSettings() {
    syncDraftFromSaved();
    popoverRef.value?.hide();
}

function canMoveDraftColumn(event: DraggableMoveEvent): boolean {
    return !isDisabledColumn(event.draggedContext.element);
}

watch(draftVisibleColumnKeys, () => {
    if (!isAccountPersistence.value) applyCookieChanges();
}, { deep: true });
watch(draftColumnOrder, () => {
    if (!isAccountPersistence.value) applyCookieChanges();
}, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ToolbarIconButton, {
      ref_key: "triggerRef",
      ref: triggerRef,
      "aria-label": "Customize table columns",
      disabled: triggerDisabled.value,
      onClick: onTriggerClick
    }, {
      default: _withCtx(() => [
        _createVNode(IconSettings)
      ]),
      _: 1
    }, 8, ["disabled"]),
    _createVNode(_unref(Popover), {
      ref_key: "popoverRef",
      ref: popoverRef,
      pt: { root: { class: 'col-customize-popover popover-align-end popover-no-flip' } }
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _cache[9] || (_cache[9] = _createElementVNode("div", { class: "col-customize__title" }, "Columns", -1)),
              _createElementVNode("button", {
                type: "button",
                class: "col-customize__reset",
                disabled: accountBusy.value,
                onClick: resetToDefaults
              }, _toDisplayString(isAccountPersistence.value ? 'Reset to Default' : 'Reset'), 9, _hoisted_5)
            ]),
            _createElementVNode("div", _hoisted_6, " Displaying " + _toDisplayString(draftVisibleCount.value) + " of " + _toDisplayString(totalColumns.value) + " columns ", 1)
          ]),
          _cache[13] || (_cache[13] = _createElementVNode("div", { class: "col-customize__divider" }, null, -1)),
          _createElementVNode("label", _hoisted_7, [
            _createVNode(_unref(ToggleSwitch), {
              inputId: "col-only-selected-toggle",
              modelValue: draftOnlyShowSelected.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((draftOnlyShowSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "col-customize__toggle-label" }, "Only Show Selected Columns", -1))
          ]),
          _cache[14] || (_cache[14] = _createElementVNode("div", { class: "col-customize__divider" }, null, -1)),
          _createVNode(_unref(IconField), { class: "col-customize__search" }, {
            default: _withCtx(() => [
              _createVNode(_unref(InputText), {
                modelValue: draftSearch.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((draftSearch).value = $event)),
                class: "col-customize__search-field",
                type: "text",
                name: "column-search",
                autocomplete: "off",
                placeholder: "Search for a Column..."
              }, null, 8, ["modelValue"]),
              (draftSearch.value)
                ? (_openBlock(), _createBlock(_unref(InputIcon), {
                    key: 0,
                    class: "pi pi-times",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (draftSearch.value = ''))
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "col-customize__divider" }, null, -1)),
          (!draftSearch.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _cache[11] || (_cache[11] = _createElementVNode("span", {
                  class: "col-customize__selectall-spacer",
                  "aria-hidden": "true"
                }, null, -1)),
                _createVNode(_unref(Checkbox), {
                  inputId: "col-select-all",
                  modelValue: selectAllModel.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectAllModel).value = $event)),
                  binary: "",
                  indeterminate: selectAllIndeterminate.value
                }, null, 8, ["modelValue", "indeterminate"]),
                _cache[12] || (_cache[12] = _createElementVNode("label", {
                  class: "col-customize__selectall-label",
                  for: "col-select-all"
                }, " Select All ", -1))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            (!draftSearch.value && !draftOnlyShowSelected.value)
              ? (_openBlock(), _createBlock(_unref(Draggable), {
                  key: 0,
                  modelValue: draftColumnOrder.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((draftColumnOrder).value = $event)),
                  tag: "ul",
                  "item-key": "identity",
                  class: "col-customize__list",
                  handle: ".col-customize__drag",
                  animation: 200,
                  move: canMoveDraftColumn,
                  "ghost-class": "col-customize__item--ghost",
                  "chosen-class": "col-customize__item--chosen"
                }, {
                  item: _withCtx(({ element: key }) => [
                    _createElementVNode("li", {
                      class: "col-customize__item",
                      "data-locked": isLockedColumn(key),
                      "data-disabled": isDisabledColumn(key)
                    }, [
                      (isDisabledColumn(key))
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: "pi pi-lock col-customize__lock",
                            role: "img",
                            "aria-label": `${labelByKey.value[key] ?? key} is unavailable on this tab`
                          }, null, 8, _hoisted_11))
                        : (_openBlock(), _createBlock(IconDrag, {
                            key: 1,
                            class: "col-customize__drag"
                          })),
                      (isDisabledColumn(key))
                        ? (_openBlock(), _createBlock(_unref(Checkbox), {
                            key: 2,
                            inputId: `col-toggle-${key}`,
                            modelValue: false,
                            binary: "",
                            disabled: ""
                          }, null, 8, ["inputId"]))
                        : (_openBlock(), _createBlock(_unref(Checkbox), {
                            key: 3,
                            inputId: `col-toggle-${key}`,
                            modelValue: draftVisibleColumnKeys.value,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((draftVisibleColumnKeys).value = $event)),
                            value: key,
                            disabled: isLockedColumn(key),
                            onClick: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
                          }, null, 8, ["inputId", "modelValue", "value", "disabled"])),
                      _createElementVNode("label", {
                        class: "col-customize__item-label",
                        for: `col-toggle-${key}`
                      }, _toDisplayString(labelByKey.value[key] ?? key), 9, _hoisted_12)
                    ], 8, _hoisted_10)
                  ]),
                  _: 1
                }, 8, ["modelValue"]))
              : (_openBlock(), _createElementBlock("ul", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDraftKeys.value, (key) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: key,
                      class: "col-customize__item",
                      "data-locked": isLockedColumn(key),
                      "data-disabled": isDisabledColumn(key)
                    }, [
                      (isDisabledColumn(key))
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: "pi pi-lock col-customize__lock",
                            role: "img",
                            "aria-label": `${labelByKey.value[key] ?? key} is unavailable on this tab`
                          }, null, 8, _hoisted_15))
                        : (_openBlock(), _createBlock(IconDrag, {
                            key: 1,
                            class: "col-customize__drag"
                          })),
                      (isDisabledColumn(key))
                        ? (_openBlock(), _createBlock(_unref(Checkbox), {
                            key: 2,
                            inputId: `col-toggle-${key}`,
                            modelValue: false,
                            binary: "",
                            disabled: ""
                          }, null, 8, ["inputId"]))
                        : (_openBlock(), _createBlock(_unref(Checkbox), {
                            key: 3,
                            inputId: `col-toggle-${key}`,
                            modelValue: draftVisibleColumnKeys.value,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((draftVisibleColumnKeys).value = $event)),
                            value: key,
                            disabled: isLockedColumn(key),
                            onClick: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["stop"]))
                          }, null, 8, ["inputId", "modelValue", "value", "disabled"])),
                      _createElementVNode("label", {
                        class: "col-customize__item-label",
                        for: `col-toggle-${key}`
                      }, _toDisplayString(labelByKey.value[key] ?? key), 9, _hoisted_16)
                    ], 8, _hoisted_14))
                  }), 128))
                ]))
          ]),
          (isAccountPersistence.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _createElementVNode("button", {
                  type: "button",
                  class: "col-customize__footer-button col-customize__footer-button--cancel",
                  disabled: accountBusy.value,
                  onClick: cancelAccountSettings
                }, " Cancel ", 8, _hoisted_18),
                _createElementVNode("button", {
                  type: "button",
                  class: "col-customize__footer-button col-customize__footer-button--apply",
                  disabled: !isDraftDirty.value || accountBusy.value,
                  onClick: saveAccountSettings
                }, _toDisplayString(_unref(preferenceStore).saving ? 'Applying...' : 'Apply'), 9, _hoisted_19)
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})